import React, { useState, useEffect } from 'react';
import { userService } from '../../../_services';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Alert, AlertDescription } from '../../ui/alert';
import { X, Loader2, Trash2, Edit2, Star } from 'lucide-react';
import star_orange from '../../../images/star-orange.svg';
import GrandParentConfirm from './GrandparentConfirmDialog';

const AssignEditGroupDialog = ({ open, onClose, onSave, poptype, title, userid, groupnumber }) => {
    const [loading, setLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');

    const [reps, setReps] = useState([]);
    const [allOverride, setAllOverride] = useState([]);
    const [overrides, setOverrides] = useState([]);

    const [selectedRep, setSelectedRep] = useState(null);
    const [repCommission, setRepCommission] = useState('');
    const [repSearchTerm, setRepSearchTerm] = useState('');
    const [showRepDropdown, setShowRepDropdown] = useState(false);

    const [editingRowIndex, setEditingRowIndex] = useState(null);
    const [overrideSearchTerms, setOverrideSearchTerms] = useState({});
    const [showOverrideDropdowns, setShowOverrideDropdowns] = useState({});

    const [grandparentEditPopup, setGrandparentEditPopup] = useState(false);
    const [grandparentsList, setGrandparentsList] = useState([]);
    const [currentOverrideData, setCurrentOverrideData] = useState(null);

    useEffect(() => {
        if (open) {
            loadData();
        } else {
            // Reset state when dialog closes
            resetState();
        }
    }, [open, poptype, userid, groupnumber]);

    const resetState = () => {
        setLoading(false);
        setSaveLoading(false);
        setSubmitMessage('');
        setReps([]);
        setAllOverride([]);
        setOverrides([]);
        setSelectedRep(null);
        setRepCommission('');
        setRepSearchTerm('');
        setShowRepDropdown(false);
        setEditingRowIndex(null);
        setOverrideSearchTerms({});
        setShowOverrideDropdowns({});
        setGrandparentEditPopup(false);
        setGrandparentsList([]);
        setCurrentOverrideData(null);
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const usermetaData = window.atob(localStorage.getItem('ajs_user_id'));
            const [userId, userName] = usermetaData.split(':');

            if (poptype === 'assign') {
                const apidata = await userService.GetRepsOverrides(userId, userName);
                if (apidata.length > 0 && apidata[0].reps.length > 0) {
                    setReps(apidata[0].reps);
                    setAllOverride(apidata[0].allOverrides);
                    // Set first rep as default for assign mode
                    const firstRep = apidata[0].reps[0];
                    setSelectedRep(firstRep);
                    setRepSearchTerm(firstRep.fullName);
                    setRepCommission('');
                }
            } else if (poptype === 'edit') {
                const apidata = await userService.GetOverridesOfUser(userId, userName, userid, groupnumber);
                if (apidata.length > 0 && apidata[0].reps.length > 0) {
                    setReps(apidata[0].reps);
                    setAllOverride(apidata[0].allOverrides);
                    const rep = apidata[0].reps[0];
                    setSelectedRep(rep);
                    setRepSearchTerm(rep.fullName);
                    setRepCommission(rep.comm);
                    setOverrides(apidata[0].overrides || []);
                }
            }
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddOverride = () => {
        setSubmitMessage('');

        // Check if there are any unsaved rows (in editing mode)
        if (editingRowIndex !== null) {
            setSubmitMessage('Please Save or remove override');
            return;
        }

        // Check if there's a blank row
        if (overrides.length > 0) {
            const hasBlankRow = overrides.some(o => !o.override || !o.comm);
            if (hasBlankRow) {
                setSubmitMessage('Please fill & add existing row first');
                return;
            }
        }

        const newOverride = { override: '', comm: '', fullName: '', parentType: 'P' };
        setOverrides([...overrides, newOverride]);
        setEditingRowIndex(overrides.length);
    };

    const handleRemoveOverride = (index) => {
        setSubmitMessage('');
        const newOverrides = [...overrides];
        newOverrides.splice(index, 1);
        setOverrides(newOverrides);
        if (editingRowIndex === index) {
            setEditingRowIndex(null);
        }
    };

    const handleEditOverride = (index) => {
        setEditingRowIndex(index);
    };

    const handleCancelEditOverride = (index) => {
        if (!overrides[index].override) {
            // If it's a new empty row, remove it
            handleRemoveOverride(index);
        } else {
            setEditingRowIndex(null);
        }
    };

    const handleOverrideSelectUser = (index, user) => {
        setSubmitMessage('');
        const newOverrides = [...overrides];
        newOverrides[index] = {
            ...newOverrides[index],
            override: user.override,
            fullName: user.fullName,
            comm: user.comm,
        };
        setOverrides(newOverrides);

        // Close dropdown
        const newShowDropdowns = { ...showOverrideDropdowns };
        newShowDropdowns[index] = false;
        setShowOverrideDropdowns(newShowDropdowns);

        // Clear search term
        const newSearchTerms = { ...overrideSearchTerms };
        newSearchTerms[index] = user.fullName;
        setOverrideSearchTerms(newSearchTerms);
    };

    const handleSaveOverride = async (index) => {
        setSubmitMessage('');
        const override = overrides[index];

        if (!override.override || !override.comm) {
            setSubmitMessage('Please fill all fields');
            return;
        }

        // Check for grandparent - store the override data for grandparent check
        const userFromList = allOverride.find(u => u.override === override.override);
        if (userFromList) {
            setCurrentOverrideData(userFromList);
            await checkGrandparent(override.override, userFromList.email);
        }

        setEditingRowIndex(null);
    };

    const checkGrandparent = async (repUserName, repEmailId) => {
        try {
            const usermetaData = window.atob(localStorage.getItem('ajs_user_id'));
            const [userId, userName] = usermetaData.split(':');

            const apidata = await userService.grandparentEditgroup(userId, userName, repUserName, repEmailId);
            if (apidata.length > 0) {
                setGrandparentsList(apidata);
                setGrandparentEditPopup(true);
            }
        } catch (error) {
            console.error('Error checking grandparent:', error);
        }
    };

    const handleAddGrandParent = () => {
        const filteredGrandparents = grandparentsList
            .filter(item => item.parent_IsAllowedForGC === 1)
            .map(item => ({
                comm: item.parent_GcComm,
                email: null,
                fullName: item.parent_FullName,
                override: item.parent_UserName,
                parentType: 'GP'
            }));

        setOverrides([...overrides, ...filteredGrandparents]);
        setGrandparentEditPopup(false);
        setGrandparentsList([]);
        setCurrentOverrideData(null);
    };

    const handleOverrideCommChange = (index, value) => {
        const newOverrides = [...overrides];
        newOverrides[index] = {
            ...newOverrides[index],
            comm: value
        };
        setOverrides(newOverrides);
    };

    const handleSaveSubmit = async () => {
        setSaveLoading(true);
        setSubmitMessage('');

        // Check if there are unsaved rows
        if (editingRowIndex !== null) {
            setSubmitMessage('Please Save or remove override');
            setSaveLoading(false);
            return;
        }

        // Validate rep commission
        if (poptype === 'assign' && !repCommission) {
            setSubmitMessage('Please enter commission');
            setSaveLoading(false);
            return;
        }

        // Build override table
        const overrideTable = overrides.map(o => ({
            Override: o.override,
            Comm: o.comm,
            ParentType: o.parentType === 'GP' ? 'Grandparent' : 'Parent'
        }));

        // Validate grandparents count
        const countGrandparents = overrideTable.filter(item => item.ParentType === 'Grandparent').length;
        if (countGrandparents > 1) {
            setSubmitMessage('Multiple grandparents are not allowed');
            setSaveLoading(false);
            return;
        }

        // Validate commission values
        for (const o of overrides) {
            if (o.comm.toString().split('.').length > 2) {
                setSubmitMessage('Please enter valid commission');
                setSaveLoading(false);
                return;
            }
            if (parseFloat(o.comm) > 2.75) {
                setSubmitMessage('Commission should not greater than Wholesaler Commission');
                setSaveLoading(false);
                return;
            }
        }

        // Call API
        try {
            const usermetaData = window.atob(localStorage.getItem('ajs_user_id'));
            const [userId, userName] = usermetaData.split(':');

            const un = selectedRep?.userName;
            const comm = poptype === 'assign' ? repCommission : repCommission;
            const uid = poptype === 'assign' ? '-1' : userid;
            const overrideJson = JSON.stringify(overrideTable);

            // Validate self override and duplicates
            const overrideUsernames = overrideTable.map(o => o.Override);
            if (overrideUsernames.includes(un)) {
                setSubmitMessage('User cannot be its self override');
                setSaveLoading(false);
                return;
            }

            const duplicates = overrideUsernames.filter((item, index) => overrideUsernames.indexOf(item) !== index);
            if (duplicates.length > 0) {
                setSubmitMessage('Same user cannot be override multiple times');
                setSaveLoading(false);
                return;
            }

            const apidata = await userService.groupengae(userId, userName, groupnumber, un, comm, uid, overrideJson);

            if (apidata.length > 0 && apidata[0].code === '1') {
                setSaveLoading(false);
                onSave();
            } else {
                setSubmitMessage('Something went Wrong!');
                setSaveLoading(false);
            }
        } catch (error) {
            console.error('Error saving:', error);
            setSubmitMessage('Something went Wrong!');
            setSaveLoading(false);
        }
    };

    const filteredReps = reps.filter(rep =>
        rep.fullName.toLowerCase().includes(repSearchTerm.toLowerCase()) ||
        rep.email.toLowerCase().includes(repSearchTerm.toLowerCase())
    );

    const getFilteredOverrides = (index) => {
        const searchTerm = overrideSearchTerms[index] || '';
        const dataSource = poptype === 'assign' ? reps : allOverride;
        return dataSource.filter(item =>
            item.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.email.toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    return (
        <>
            <Dialog open={open} onOpenChange={onClose}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle>{title}</DialogTitle>
                    </DialogHeader>

                    {loading ? (
                        <div className="flex items-center justify-center p-8">
                            <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
                        </div>
                    ) : (
                        <div className="flex-1 overflow-y-auto space-y-6 px-1">
                            {/* Rep Selection and Commission */}
                            {reps.length > 0 && (
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Select User</Label>
                                        {poptype === 'assign' ? (
                                            <div className="relative">
                                                <Input
                                                    value={repSearchTerm}
                                                    onChange={(e) => {
                                                        setRepSearchTerm(e.target.value);
                                                        setShowRepDropdown(true);
                                                    }}
                                                    onFocus={() => setShowRepDropdown(true)}
                                                    placeholder="Search user..."
                                                />
                                                {showRepDropdown && filteredReps.length > 0 && (
                                                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                                                        {filteredReps.map((rep) => (
                                                            <div
                                                                key={rep.userName}
                                                                className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                                                onClick={() => {
                                                                    setSelectedRep(rep);
                                                                    setRepSearchTerm(rep.fullName);
                                                                    setShowRepDropdown(false);
                                                                }}
                                                            >
                                                                {rep.fullName} {rep.email}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <Input
                                                value={repSearchTerm}
                                                disabled
                                                className="bg-gray-100"
                                            />
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Commission $</Label>
                                        <Input
                                            type="text"
                                            value={repCommission}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (/^[0-9.]*$/.test(value)) {
                                                    setRepCommission(value);
                                                }
                                            }}
                                            disabled={poptype === 'edit'}
                                            placeholder="Enter commission"
                                            className={poptype === 'edit' ? 'bg-gray-100' : ''}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Overrides Section */}
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-semibold">Override</h3>
                                    {overrides.length > 0 && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={handleAddOverride}
                                            disabled={editingRowIndex !== null}
                                        >
                                            Add New Override
                                        </Button>
                                    )}
                                </div>

                                {overrides.length === 0 ? (
                                    <div className="text-center py-8 border rounded-lg bg-gray-50">
                                        <p className="mb-4 text-gray-600">No Override added yet</p>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={handleAddOverride}
                                        >
                                            Add New Override
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="border rounded-lg overflow-hidden">
                                        {/* Table Header - Hidden on mobile */}
                                        <div className="hidden md:grid md:grid-cols-[minmax(200px,2fr)_minmax(150px,1fr)_160px] gap-4 p-3 bg-gray-100 font-semibold text-sm">
                                            <div>Select User</div>
                                            <div>Commission $</div>
                                            <div>Actions</div>
                                        </div>
                                        
                                        {/* Table Body */}
                                        <div className="max-h-[300px] overflow-y-auto">
                                            {overrides.map((override, index) => {
                                                const isEditing = editingRowIndex === index;
                                                const isNewRow = !override.override;

                                                return (
                                                    <div
                                                        key={index}
                                                        className={`grid grid-cols-1 md:grid-cols-[minmax(200px,2fr)_minmax(150px,1fr)_160px] gap-3 md:gap-4 p-3 border-t items-center ${isEditing ? 'bg-blue-50' : ''}`}
                                                    >
                                                        {/* Select User Column */}
                                                        {isEditing ? (
                                                            <div className="relative">
                                                                <Input
                                                                    value={overrideSearchTerms[index] || override.fullName || ''}
                                                                    onChange={(e) => {
                                                                        const newTerms = { ...overrideSearchTerms };
                                                                        newTerms[index] = e.target.value;
                                                                        setOverrideSearchTerms(newTerms);
                                                                        const newDropdowns = { ...showOverrideDropdowns };
                                                                        newDropdowns[index] = true;
                                                                        setShowOverrideDropdowns(newDropdowns);
                                                                    }}
                                                                    onFocus={() => {
                                                                        const newDropdowns = { ...showOverrideDropdowns };
                                                                        newDropdowns[index] = true;
                                                                        setShowOverrideDropdowns(newDropdowns);
                                                                    }}
                                                                    placeholder="Select user..."
                                                                />
                                                                {showOverrideDropdowns[index] && (
                                                                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-auto">
                                                                        {getFilteredOverrides(index).map((user) => (
                                                                            <div
                                                                                key={user.override + '_' + user.email}
                                                                                className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                                                                                onClick={() => handleOverrideSelectUser(index, user)}
                                                                            >
                                                                                {user.fullName} {user.email}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-sm">{override.fullName}</span>
                                                                <div className="relative inline-flex items-center gap-1 group">
                                                                    {override.parentType === 'GP' ? (
                                                                        <>
                                                                            <img src={star_orange} alt="" className="w-4 h-4" />
                                                                            <img src={star_orange} alt="" className="w-4 h-4" />
                                                                        </>
                                                                    ) : (
                                                                        <img src={star_orange} alt="" className="w-4 h-4" />
                                                                    )}
                                                                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-black text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                                                                        {override.parentType === 'GP' ? 'Grandparent' : 'Parent'}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Commission Column */}
                                                        <Input
                                                            type="text"
                                                            value={override.comm}
                                                            onChange={(e) => {
                                                                const value = e.target.value;
                                                                if (/^[0-9.]*$/.test(value)) {
                                                                    handleOverrideCommChange(index, value);
                                                                }
                                                            }}
                                                            disabled={!isEditing}
                                                            placeholder="Commission $"
                                                            className={!isEditing ? 'bg-gray-100' : ''}
                                                        />

                                                        {/* Actions Column */}
                                                        <div className="flex gap-2">
                                                            {isEditing ? (
                                                                <>
                                                                    <Button
                                                                        type="button"
                                                                        size="sm"
                                                                        onClick={() => handleSaveOverride(index)}
                                                                        className="flex-1 md:flex-initial"
                                                                    >
                                                                        {isNewRow ? 'Add' : 'Save'}
                                                                    </Button>
                                                                    <Button
                                                                        type="button"
                                                                        variant="outline"
                                                                        size="sm"
                                                                        onClick={() => handleCancelEditOverride(index)}
                                                                        className="flex-1 md:flex-initial"
                                                                    >
                                                                        Cancel
                                                                    </Button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => handleEditOverride(index)}
                                                                        disabled={editingRowIndex !== null}
                                                                        className="flex-1 md:flex-initial"
                                                                    >
                                                                        <Edit2 className="h-4 w-4" />
                                                                    </Button>
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => handleRemoveOverride(index)}
                                                                        disabled={editingRowIndex !== null}
                                                                        className="flex-1 md:flex-initial"
                                                                    >
                                                                        <Trash2 className="h-4 w-4" />
                                                                    </Button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Error Message */}
                            {submitMessage && (
                                <Alert variant="destructive">
                                    <AlertDescription>{submitMessage}</AlertDescription>
                                </Alert>
                            )}
                        </div>
                    )}

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            disabled={saveLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={handleSaveSubmit}
                            disabled={saveLoading || loading}
                        >
                            {saveLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Save & Submit
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {grandparentEditPopup && (
                <GrandParentConfirm
                    grandparentsList={grandparentsList}
                    toggle_gp_popup={() => {
                        setGrandparentEditPopup(false);
                        setGrandparentsList([]);
                        setCurrentOverrideData(null);
                    }}
                    title={`Grandparents for ${currentOverrideData?.email || ''}`}
                    handleAddGrandParent={handleAddGrandParent}
                />
            )}
        </>
    );
};

export default AssignEditGroupDialog;
